import axios from 'axios';
import { defineStore } from 'pinia';
import { ref, watch } from 'vue';
import {
  dataPortalViewsApi,
  type DataPortalViewSummary,
} from '../api/data-portal-views.api';
import { useDataPortalAuthStore } from '../auth/data-portal-auth.store';

export const useDataPortalViewsStore = defineStore('dataPortalViews', () => {
  const auth = useDataPortalAuthStore();
  const views = ref<DataPortalViewSummary[]>([]);
  const loading = ref(false);
  const loaded = ref(false);
  const error = ref<unknown>(null);
  const loadedForSubject = ref<string | null>(null);
  const loadedForFingerprint = ref<string | null>(null);
  let controller: AbortController | null = null;
  let generation = 0;
  let activeRequest: { fingerprint: string; promise: Promise<boolean> } | null =
    null;

  auth.$onAction(({ name, after }) => {
    if (name !== 'logout') return;
    reset();
    after(() => {
      if (auth.user) void load();
    });
  });

  function reset(): void {
    generation += 1;
    controller?.abort();
    controller = null;
    activeRequest = null;
    views.value = [];
    loading.value = false;
    loaded.value = false;
    error.value = null;
    loadedForSubject.value = null;
    loadedForFingerprint.value = null;
  }

  function currentFingerprint(): string | null {
    const user = auth.user;
    if (!user?.sub) return null;
    return JSON.stringify([
      user.sub,
      [...user.roles].sort(),
      [...user.groups].sort(),
    ]);
  }

  function isCurrent(requestGeneration: number, fingerprint: string): boolean {
    return (
      generation === requestGeneration && currentFingerprint() === fingerprint
    );
  }

  async function request(
    fingerprint: string,
    signal: AbortSignal,
    requestGeneration: number,
    allowSessionRenewal = true,
  ): Promise<DataPortalViewSummary[]> {
    try {
      return await dataPortalViewsApi.listViews(signal);
    } catch (requestError) {
      if (
        allowSessionRenewal &&
        axios.isAxiosError(requestError) &&
        requestError.response?.status === 401 &&
        isCurrent(requestGeneration, fingerprint) &&
        !signal.aborted
      ) {
        const renewed = await auth.loadUser();
        if (
          renewed &&
          isCurrent(requestGeneration, fingerprint) &&
          !signal.aborted
        ) {
          return request(fingerprint, signal, requestGeneration, false);
        }
      }
      throw requestError;
    }
  }

  function load(force = false): Promise<boolean> {
    const fingerprint = currentFingerprint();
    if (!fingerprint) {
      reset();
      return Promise.resolve(false);
    }
    if (
      loaded.value &&
      loadedForFingerprint.value === fingerprint &&
      !force
    ) {
      return Promise.resolve(true);
    }
    if (activeRequest?.fingerprint === fingerprint && !force) {
      return activeRequest.promise;
    }

    generation += 1;
    const requestGeneration = generation;
    controller?.abort();
    controller = new AbortController();
    const signal = controller.signal;
    views.value = [];
    loaded.value = false;
    loadedForSubject.value = null;
    loadedForFingerprint.value = null;
    loading.value = true;
    error.value = null;

    const promise = request(fingerprint, signal, requestGeneration)
      .then((result) => {
        if (!isCurrent(requestGeneration, fingerprint) || signal.aborted) {
          return false;
        }
        views.value = result;
        loaded.value = true;
        loadedForSubject.value = auth.user?.sub ?? null;
        loadedForFingerprint.value = fingerprint;
        return true;
      })
      .catch((requestError) => {
        if (
          !isCurrent(requestGeneration, fingerprint) ||
          axios.isCancel(requestError)
        ) {
          return false;
        }
        views.value = [];
        loaded.value = false;
        loadedForSubject.value = null;
        loadedForFingerprint.value = null;
        error.value = requestError;
        return false;
      })
      .finally(() => {
        if (!isCurrent(requestGeneration, fingerprint)) return;
        loading.value = false;
        controller = null;
        activeRequest = null;
      });
    activeRequest = { fingerprint, promise };
    return promise;
  }

  watch(
    currentFingerprint,
    (fingerprint, previousFingerprint) => {
      if (fingerprint === previousFingerprint) return;
      reset();
      if (fingerprint) void load();
    },
    { flush: 'sync' },
  );

  watch(
    () => auth.status,
    (status) => {
      if (status === 'anonymous' && !auth.user) {
        error.value = new Error('Data portal session expired');
      }
    },
    { flush: 'sync' },
  );

  return {
    views,
    loading,
    loaded,
    error,
    loadedForSubject,
    loadedForFingerprint,
    load,
    reset,
  };
});
